import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { gestoriaAPI, talleresAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Scale, Send, Paperclip, X, Loader2, CheckCircle2, Plane, Search,
  FileText, Repeat, Award, ClipboardCheck, Wrench as WrenchIcon,
  Stamp, BadgeCheck, Sparkles, ArrowRight, Bell, Building2, Award as AwardIcon,
} from 'lucide-react';

const QUIEN_CONTACTA = [
  { value: 'piloto', label: 'Piloto' },
  { value: 'dueno_aeronave', label: 'Dueño de Aeronave' },
  { value: 'dueno_tar', label: 'Dueño de un TAR' },
  { value: 'rt_tar', label: 'RT / Administrador de un TAR' },
  { value: 'mecanico_tar', label: 'Mecánico de un TAR' },
  { value: 'otro', label: 'Otro' },
];

const TIPO_TRAMITE = [
  { value: 'transferencia_aeronaves', label: 'Transferencia de aeronaves' },
  { value: 'matriculacion', label: 'Matriculación' },
  { value: 'cese_bandera_certificacion', label: 'Cese de bandera y certificación' },
  { value: 'habilitacion_tar', label: 'Habilitación de TAR' },
  { value: 'solicitud_alcances', label: 'Solicitud de alcances' },
  { value: 'adquisicion_tool_especial', label: 'Adquisición de Tool Especial' },
  { value: 'licencia_mma_mera', label: 'Tramitación de Licencia MMA / MERA' },
  { value: 'manual_taller_mpi', label: 'Diseño y confección Manual de Taller (MPI)' },
  { value: 'otro', label: 'Otro' },
];

const SERVICIOS = [
  { icon: Repeat, titulo: 'Transferencia de aeronaves', desc: 'Te guiamos en cada paso de la transferencia hasta la inscripción final.' },
  { icon: Stamp, titulo: 'Matriculación', desc: 'Inscripción y matriculación de aeronaves ante la Autoridad Aeronáutica.' },
  { icon: BadgeCheck, titulo: 'Cese de bandera y certificación', desc: 'Tramitamos el cese y la certificación completa de tu aeronave.' },
  { icon: ClipboardCheck, titulo: 'Habilitación de TAR', desc: 'Habilitamos tu Taller Aeronáutico desde cero o ampliamos alcances.' },
  { icon: FileText, titulo: 'Solicitud de alcances', desc: 'Gestionamos nuevas habilitaciones y alcances para tu TAR.' },
  { icon: WrenchIcon, titulo: 'Adquisición de Tool Especial', desc: 'Asesoramiento técnico y gestión de tooling aeronáutico.' },
  { icon: Award, titulo: 'Licencia MMA / MERA', desc: 'Tramitación y seguimiento de licencias de mantenimiento.' },
  { icon: FileText, titulo: 'Manual de Taller (MPI)', desc: 'Diseño y confección del Manual de Procedimientos de Inspección.' },
  { icon: Sparkles, titulo: 'Oficina Técnica integral', desc: 'Todo trámite que requiera una Oficina Técnica Aeronáutica.' },
];

const MAX_FILES = 3;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

// ============================================
// Combobox de TAR — búsqueda en backend (tema CLARO — vive dentro del modal)
// ============================================
const TarCombobox = ({ value, textValue, onChange }) => {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const [display, setDisplay] = useState(textValue || '');

  useEffect(() => { if (textValue !== undefined) setDisplay(textValue || ''); }, [textValue]);

  useEffect(() => {
    const h = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setDisplay(val);
    onChange({ tar_id: null, tar_texto: val });
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await gestoriaAPI.buscarTalleres(val);
        setResults(res.data?.results || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
  };

  const handleSelect = (t) => {
    setDisplay(t.razon_social);
    onChange({ tar_id: t.id, tar_texto: null, tar_razon_social: t.razon_social, tar_anac: t.numero_anac });
    setOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <Input value={display} onChange={handleInputChange} onFocus={() => display && setOpen(true)} placeholder="Escribí razón social o N° ANAC..." className="pl-9" data-testid="gestoria-tar-combobox" />
        {value && <Badge className="absolute right-2 top-1/2 -translate-y-1/2 bg-violet-100 text-violet-700 border border-violet-200">Registrado</Badge>}
      </div>
      <p className="text-xs text-slate-500 mt-1">Completar si corresponde</p>
      {open && (results.length > 0 || loading || display.trim().length >= 2) && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
          {loading && <div className="p-3 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Buscando...</div>}
          {!loading && results.length === 0 && display.trim().length >= 2 && (
            <div className="p-3 text-sm text-slate-500">Sin coincidencias. Se guardará como: <span className="text-violet-700 font-medium">"{display}"</span></div>
          )}
          {!loading && results.map((r) => (
            <button key={r.id} type="button" onClick={() => handleSelect(r)} className="w-full text-left px-3 py-2 hover:bg-violet-50 border-b border-slate-100 last:border-0 flex items-center gap-3 group" data-testid={`gestoria-tar-option-${r.id}`}>
              <Building2 className="h-4 w-4 text-violet-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-900 truncate group-hover:text-violet-700">{r.razon_social}</p>
                <p className="text-xs text-slate-500">ANAC {r.numero_anac} {r.provincia ? `• ${r.provincia}` : ''}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// Formulario (tema claro — dentro del modal)
// ============================================
const INITIAL_FORM = {
  nombre_completo: '', dni: '', telefono: '', email: '',
  quien_contacta: '', quien_contacta_otro: '',
  tipo_tramite: '', tipo_tramite_otro: '',
  tar_id: null, tar_texto: null, matricula: '', texto: '',
};

const SolicitudForm = ({ tipoSolicitud, onClose }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const fileInputRef = useRef(null);
  const esTramite = tipoSolicitud === 'tramite';
  const labelTexto = esTramite ? 'Nota' : 'Consulta';
  const labelTipo = esTramite ? 'Tipo de trámite' : 'Motivo de consulta';
  const labelBoton = esTramite ? 'Enviar solicitud de trámite' : 'Enviar consulta';

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleFiles = (e) => {
    const incoming = Array.from(e.target.files || []);
    if (files.length + incoming.length > MAX_FILES) { toast.error(`Máximo ${MAX_FILES} archivos`); return; }
    const tooBig = incoming.find((f) => f.size > MAX_FILE_SIZE);
    if (tooBig) { toast.error(`"${tooBig.name}" supera los 10MB`); return; }
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
    const bad = incoming.find((f) => !allowed.includes(f.type));
    if (bad) { toast.error(`Tipo no permitido: "${bad.name}" (solo PNG, JPG, PDF)`); return; }
    setFiles((p) => [...p, ...incoming]);
    e.target.value = '';
  };
  const removeFile = (idx) => setFiles((p) => p.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre_completo || !form.dni || !form.telefono || !form.email) { toast.error('Completá todos los campos obligatorios'); return; }
    if (!form.quien_contacta) { toast.error('Seleccioná quién nos contacta'); return; }
    if (form.quien_contacta === 'otro' && !form.quien_contacta_otro.trim()) { toast.error('Especificá quién nos contacta'); return; }
    if (!form.tipo_tramite) { toast.error(`Seleccioná el ${labelTipo.toLowerCase()}`); return; }
    if (form.tipo_tramite === 'otro' && !form.tipo_tramite_otro.trim()) { toast.error(`Especificá el ${labelTipo.toLowerCase()}`); return; }
    if (!form.texto.trim()) { toast.error(`Escribí tu ${labelTexto.toLowerCase()}`); return; }

    const payload = {
      tipo_solicitud: tipoSolicitud,
      nombre_completo: form.nombre_completo.trim(), dni: form.dni.trim(),
      telefono: form.telefono.trim(), email: form.email.trim(),
      quien_contacta: form.quien_contacta,
      quien_contacta_otro: form.quien_contacta === 'otro' ? form.quien_contacta_otro.trim() : null,
      tipo_tramite: form.tipo_tramite,
      tipo_tramite_otro: form.tipo_tramite === 'otro' ? form.tipo_tramite_otro.trim() : null,
      tar_id: form.tar_id,
      tar_texto: form.tar_id ? null : (form.tar_texto || null),
      matricula: form.matricula.trim() || null,
      texto: form.texto.trim(),
    };

    setSubmitting(true);
    try {
      const res = await gestoriaAPI.crearSolicitud(payload, files);
      toast.success(esTramite ? '¡Solicitud enviada!' : '¡Consulta enviada!');
      setDone(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al enviar';
      toast.error(typeof msg === 'string' ? msg : 'Error al enviar');
    } finally { setSubmitting(false); }
  };

  if (done) {
    const trackUrl = done.token_seguimiento ? `/gestoria/seguimiento/${done.token_seguimiento}` : null;
    return (
      <div className="py-10 text-center space-y-4" data-testid={`gestoria-form-success-${tipoSolicitud}`}>
        <div className="mx-auto bg-violet-100 p-4 rounded-full w-fit border border-violet-200">
          <CheckCircle2 className="h-12 w-12 text-violet-600" />
        </div>
        <h3 className="text-2xl font-bold text-slate-900">¡Gracias!</h3>
        <p className="text-slate-600 max-w-md mx-auto">
          Recibimos tu {esTramite ? 'solicitud' : 'consulta'}. Te enviamos un email de confirmación y nuestro equipo se pondrá en contacto en las próximas horas.
        </p>
        {trackUrl && (
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 max-w-md mx-auto">
            <p className="text-sm text-slate-700 mb-2">Podés seguir el estado en tiempo real desde:</p>
            <Button asChild variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-100">
              <Link to={trackUrl}>Ver seguimiento →</Link>
            </Button>
          </div>
        )}
        <Button onClick={onClose} className="bg-violet-600 hover:bg-violet-500 text-white">Cerrar</Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid={`gestoria-form-${tipoSolicitud}`}>
      <div className="grid md:grid-cols-2 gap-3">
        <div className="space-y-1"><Label>Nombre Completo *</Label><Input value={form.nombre_completo} onChange={(e) => update('nombre_completo', e.target.value)} data-testid="gestoria-field-nombre" required /></div>
        <div className="space-y-1"><Label>DNI *</Label><Input value={form.dni} onChange={(e) => update('dni', e.target.value)} data-testid="gestoria-field-dni" required /></div>
        <div className="space-y-1"><Label>Teléfono de contacto *</Label><Input value={form.telefono} onChange={(e) => update('telefono', e.target.value)} data-testid="gestoria-field-telefono" required /></div>
        <div className="space-y-1"><Label>Mail *</Label><Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} data-testid="gestoria-field-email" required /></div>
      </div>
      <div className="space-y-1">
        <Label>¿Quién nos contacta? *</Label>
        <Select value={form.quien_contacta} onValueChange={(v) => update('quien_contacta', v)}>
          <SelectTrigger data-testid="gestoria-field-quien"><SelectValue placeholder="Seleccioná una opción" /></SelectTrigger>
          <SelectContent>{QUIEN_CONTACTA.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
        {form.quien_contacta === 'otro' && <Input value={form.quien_contacta_otro} onChange={(e) => update('quien_contacta_otro', e.target.value)} placeholder="Especificá..." className="mt-2" data-testid="gestoria-field-quien-otro" />}
      </div>
      <div className="space-y-1">
        <Label>{labelTipo} *</Label>
        <Select value={form.tipo_tramite} onValueChange={(v) => update('tipo_tramite', v)}>
          <SelectTrigger data-testid="gestoria-field-tipo"><SelectValue placeholder="Seleccioná una opción" /></SelectTrigger>
          <SelectContent>{TIPO_TRAMITE.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
        {form.tipo_tramite === 'otro' && <Input value={form.tipo_tramite_otro} onChange={(e) => update('tipo_tramite_otro', e.target.value)} placeholder="Especificá..." className="mt-2" data-testid="gestoria-field-tipo-otro" />}
      </div>
      <div className="space-y-1"><Label>Nombre del TAR</Label><TarCombobox value={form.tar_id} textValue={form.tar_texto} onChange={(p) => setForm((prev) => ({ ...prev, ...p }))} /></div>
      <div className="space-y-1">
        <Label>Matrícula de la aeronave</Label>
        <Input value={form.matricula} onChange={(e) => update('matricula', e.target.value)} placeholder="Ej: LV-ABC" data-testid="gestoria-field-matricula" />
        <p className="text-xs text-slate-500">Completar si corresponde</p>
      </div>
      <div className="space-y-1"><Label>{labelTexto} *</Label><Textarea value={form.texto} onChange={(e) => update('texto', e.target.value)} rows={5} className="resize-y min-h-[120px]" data-testid="gestoria-field-texto" required /></div>
      <div className="space-y-1">
        <Label>Adjuntos</Label>
        <div className="flex items-center gap-2 flex-wrap">
          <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={files.length >= MAX_FILES} data-testid="gestoria-attach-btn">
            <Paperclip className="mr-2 h-4 w-4" /> Adjuntar archivo
          </Button>
          <span className="text-xs text-slate-500">{files.length}/{MAX_FILES} • PNG, JPG, PDF — máx. 10MB c/u</span>
          <input ref={fileInputRef} type="file" hidden multiple accept=".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf" onChange={handleFiles} />
        </div>
        {files.length > 0 && (
          <div className="mt-2 space-y-1.5">
            {files.map((f, i) => (
              <div key={i} className="flex items-center justify-between bg-slate-50 border rounded-md px-3 py-2 text-sm">
                <span className="truncate text-slate-800" title={f.name}>{f.name}</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-slate-500">{(f.size / 1024).toFixed(0)} KB</span>
                  <button type="button" onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="flex gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
        <Button type="submit" disabled={submitting} className="flex-[2] bg-violet-600 hover:bg-violet-500 text-white" data-testid={`gestoria-submit-${tipoSolicitud}`}>
          {submitting ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Enviando...</> : <><Send className="mr-2 h-5 w-5" /> {labelBoton}</>}
        </Button>
      </div>
    </form>
  );
};

// ============================================
// Banner Beneficios — tema CLARO (vive en franja blanca)
// ============================================
const BeneficiosBanner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [miTaller, setMiTaller] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (user?.taller_id) {
        try { const res = await talleresAPI.getOne(user.taller_id); setMiTaller(res.data); } catch {}
      }
    };
    load();
  }, [user]);

  const nivel = miTaller?.nivel_suscripcion;
  const planes = ['status_report', 'estandar', 'estandar_pro', 'premium'];
  const tienePlan = user && nivel && planes.includes(nivel);

  let c;
  if (!tienePlan) {
    c = { texto: <>Tu taller merece lo mejor — vos y el avión también.<br />Suscribite y accedé a descuentos exclusivos, tiempos de respuesta prioritarios y atención personalizada en todos nuestros servicios.</>, boton: 'Quiero mis beneficios — conocé los planes', onClick: () => navigate('/suscripcion') };
  } else if (nivel === 'status_report') {
    c = { texto: <>Accedés a <strong>consultoría sin costo</strong>.<br />Actualizate al plan <strong>Standard</strong> para obtener descuentos en la gestión de tus trámites y atención telefónica personalizada.</>, boton: 'Quiero cambiar al plan Standard', onClick: () => navigate('/suscripcion?nivel=estandar') };
  } else if (nivel === 'estandar' || nivel === 'estandar_pro') {
    c = { texto: <>Tus consultas son sin costo, tenés atención telefónica personalizada y descuentos en todos tus trámites en nuestra Oficina Técnica online.<br />Actualizate al plan <strong>Premium</strong> y sumá descuentos especiales en el SIGMA — Sistema de Gestión de Mantenimiento Aeronáutico.</>, boton: 'Quiero cambiar al plan Premium', onClick: () => navigate('/suscripcion?nivel=premium') };
  } else {
    c = { texto: <>Tenés todos los beneficios del plan Standard más descuentos especiales en el SIGMA — Sistema de Gestión de Mantenimiento Aeronáutico. <strong>Estás aprovechando al máximo la plataforma C&amp;M.</strong></>, boton: 'Ver mis beneficios completos', onClick: () => navigate('/panel') };
  }

  return (
    <div className="rounded-2xl p-6 md:p-8 bg-gradient-to-br from-violet-50 via-purple-50 to-white border-2 border-violet-200 shadow-lg shadow-violet-500/5">
      <div className="flex items-start gap-4 flex-col md:flex-row">
        <div className="bg-gradient-to-br from-violet-500 to-purple-700 p-3 rounded-xl shadow-lg shadow-violet-500/30 flex-shrink-0">
          <Award className="h-7 w-7 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-1">
            Beneficios exclusivos <span className="text-violet-600">para clientes C&M</span>
          </h3>
          <p className="text-slate-600 mb-3 text-sm">
            Nuestros suscriptores acceden a condiciones preferenciales y atención prioritaria en todos los servicios de gestoría, de acuerdo a su plan.
          </p>
          <p className="text-slate-800 leading-relaxed mb-5">{c.texto}</p>
          <Button onClick={c.onClick} className="bg-violet-600 hover:bg-violet-500 text-white shadow-lg shadow-violet-500/30" data-testid="gestoria-benefits-cta">
            {c.boton} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// Banner SIGMA — NEGRO + DORADO (teaser del futuro modulo)
// ============================================
const SigmaBanner = () => {
  const [open, setOpen] = useState(false);
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!nombre.trim() || !email.trim()) return;
    setSending(true);
    try {
      const res = await gestoriaAPI.sigmaInteresado({ nombre: nombre.trim(), email: email.trim() });
      toast.success(res.data?.mensaje || '¡Te avisamos cuando esté disponible!');
      setOpen(false); setNombre(''); setEmail('');
    } catch (err) {
      const msg = err.response?.data?.detail || 'No pudimos registrarte. Probá de nuevo.';
      toast.error(typeof msg === 'string' ? msg : 'No pudimos registrarte');
    } finally { setSending(false); }
  };

  return (
    <>
      <div className="rounded-2xl p-8 md:p-10 bg-black border-2 border-amber-500/40 shadow-2xl shadow-amber-500/10 relative overflow-hidden">
        {/* Decor dorado */}
        <div className="absolute -top-16 -right-16 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute top-4 right-4">
          <Badge className="bg-amber-500/10 text-amber-400 border border-amber-500/40 uppercase tracking-wider text-xs">Próximamente</Badge>
        </div>
        <div className="flex items-start gap-5 flex-col md:flex-row relative">
          {/* Avion con contorno dorado */}
          <div className="flex-shrink-0 bg-black p-4 rounded-2xl border-2 border-amber-500/60 shadow-lg shadow-amber-500/20">
            <Plane className="h-10 w-10 text-amber-400" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <h3 className="text-2xl md:text-3xl font-bold text-amber-400" style={{ textShadow: '0 0 20px rgba(251, 191, 36, 0.25)' }}>SIGMA</h3>
            <p className="text-amber-300/90 font-medium mb-2">Sistema de Gestión de Mantenimiento Aeronáutico</p>
            <p className="text-slate-400 italic mb-4">Una nueva forma de administrar tu operación</p>
            <p className="text-slate-300 leading-relaxed mb-6">
              Estamos desarrollando una herramienta integral para la gestión operativa de flotas aeronáuticas: seguimiento de vencimientos, control de mantenimientos, gestión documental de cada aeronave, checklists digitales y mucho más — todo en un solo lugar, dentro de la plataforma que ya conocés. Próximamente disponible para los clientes de C&amp;M Servicios Aeronáuticos.
            </p>
            <Button onClick={() => setOpen(true)} variant="outline" className="border-amber-500/60 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300 hover:border-amber-400" data-testid="gestoria-sigma-notify-btn">
              <Bell className="mr-2 h-4 w-4" /> Avisame cuando esté disponible
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="gestoria-sigma-modal">
          <DialogHeader>
            <DialogTitle>Avisame cuando SIGMA esté disponible</DialogTitle>
            <DialogDescription>Dejanos tus datos y te avisamos ni bien esté listo para usar.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div><Label>Nombre *</Label><Input value={nombre} onChange={(e) => setNombre(e.target.value)} required data-testid="gestoria-sigma-nombre" /></div>
            <div><Label>Mail *</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="gestoria-sigma-email" /></div>
            <DialogFooter className="mt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={sending} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="gestoria-sigma-submit">
                {sending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enviando...</> : 'Quiero que me avisen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ============================================
// PAGE
// ============================================
const GestoriaPage = () => {
  const [openForm, setOpenForm] = useState(null); // 'tramite' | 'asesoramiento' | null
  const location = useLocation();

  // Scroll suave al hash (#sigma) cuando se llega desde el Home
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      // pequeño delay para esperar el render completo
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
      return () => clearTimeout(t);
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" data-testid="gestoria-page">
      {/* HERO violeta — compacto, simétrico con Capacitaciones */}
      <section className="relative overflow-hidden bg-gradient-to-r from-violet-700 to-violet-800 text-white py-20">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <div className="bg-white/10 rounded-full p-4 w-fit mx-auto mb-4">
            <Scale className="h-12 w-12" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">
            Gestoría Aeronáutica
          </h1>
          <p className="text-violet-100 text-lg max-w-2xl mx-auto">
            Trámites, habilitaciones y asesoramiento ante la ANAC.<br className="hidden sm:block" />
            Tu Oficina Técnica Aeronáutica online, donde estés.
          </p>
        </div>
        {/* Gradient transition violet → dark, suaviza el borde inferior */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-slate-950 to-transparent pointer-events-none" />
      </section>

      {/* SLOGAN — Quote elegante con la propuesta de valor */}
      <section className="relative py-16 md:py-20 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-violet-600/5 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <div className="relative inline-block mb-5">
            <Plane className="h-14 w-14 text-violet-500/70 mx-auto" strokeWidth={1.5} />
            <BadgeCheck
              className="h-7 w-7 text-violet-300 absolute -bottom-1 -right-2 bg-slate-950 rounded-full p-0.5 ring-2 ring-slate-950"
              strokeWidth={2}
              data-testid="gestoria-slogan-badge-icon"
            />
          </div>
          <p className="text-xl md:text-2xl text-white leading-relaxed font-light tracking-wide">
            Ahora <strong className="font-semibold text-violet-300">C&amp;M</strong> pone una{' '}
            <em className="not-italic font-semibold bg-gradient-to-r from-violet-400 to-pink-300 bg-clip-text text-transparent">
              Oficina Técnica Aeronáutica
            </em>{' '}
            en tus manos, disponible desde cualquier dispositivo y en cualquier punto del país.
          </p>
          <p className="text-base md:text-lg text-slate-400 leading-relaxed mt-4 max-w-3xl mx-auto">
            Brindamos servicios de gestoría y asesoramiento para que vos te concentres en volar o desarrollar
            tus actividades de mantenimiento, mientras nosotros nos ocupamos de los trámites.
          </p>
          <div className="mt-8 mx-auto h-[2px] w-24 bg-gradient-to-r from-transparent via-violet-500 to-transparent" />
        </div>
      </section>

      {/* SERVICIOS — tema OSCURO (como antes) */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-violet-500/10 text-violet-300 px-4 py-1.5 rounded-full border border-violet-500/30 text-sm mb-4">
              <Sparkles className="h-4 w-4" /> Nuestros servicios
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              Todo lo que tu taller necesita,
              <span className="block text-violet-400">en un solo lugar</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICIOS.map((s, i) => (
              <Card key={i} className="bg-gradient-to-br from-violet-950/40 to-slate-900/60 border border-violet-900/40 hover:border-violet-500/60 transition-all duration-300 hover:shadow-xl hover:shadow-violet-500/10 group" data-testid={`gestoria-servicio-${i}`}>
                <CardContent className="p-6">
                  <div className="bg-violet-500/15 p-3 rounded-xl w-fit mb-4 group-hover:bg-violet-500/25 transition-colors border border-violet-500/30">
                    <s.icon className="h-6 w-6 text-violet-300" />
                  </div>
                  <h3 className="text-white font-semibold mb-1">{s.titulo}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* NUESTRO EQUIPO — credibilidad antes del CTA */}
      <section className="relative py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="rounded-2xl bg-gradient-to-br from-violet-950/40 via-slate-900/60 to-slate-950 border border-violet-900/40 p-8 md:p-10 relative overflow-hidden">
            {/* Decoración */}
            <div className="absolute -right-12 -top-12 w-48 h-48 bg-violet-500/10 rounded-full blur-3xl" />
            <div className="relative flex items-start gap-5 flex-col md:flex-row">
              <div className="flex-shrink-0 bg-gradient-to-br from-violet-500/30 to-purple-700/20 p-4 rounded-2xl border border-violet-400/30">
                <AwardIcon className="h-9 w-9 text-violet-300" />
              </div>
              <div className="flex-1">
                <p className="text-violet-300 text-xs font-bold tracking-[0.2em] uppercase mb-2">Nuestro equipo</p>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                  Profesionales con trayectoria
                  <span className="block text-violet-400">en la industria aeronáutica</span>
                </h2>
                <p className="text-slate-300 leading-relaxed text-base md:text-lg">
                  Nuestro equipo de profesionales, con amplia trayectoria en la industria aeronáutica, está
                  comprometido con ofrecerte soluciones eficientes, seguras y alineadas con las exigencias de la
                  <strong className="text-white"> Autoridad Aeronáutica</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FRANJA BLANCA — pregunta + botones arriba, beneficios abajo */}
      <section className="py-16 md:py-20 bg-white" id="formularios">
        <div className="max-w-5xl mx-auto px-4 space-y-8">
          {/* Pregunta + botones */}
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              ¿En qué te podemos <span className="text-violet-600">ayudar</span>?
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto mb-8">
              Iniciá tu trámite o consultanos lo que necesites. Nuestro equipo te responde en pocas horas.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              <Button onClick={() => setOpenForm('tramite')} className="h-auto py-6 bg-violet-600 hover:bg-violet-500 text-white shadow-lg shadow-violet-500/30 flex flex-col items-center gap-2 whitespace-normal" data-testid="gestoria-btn-tramite">
                <ClipboardCheck className="h-7 w-7" />
                <span className="text-base font-semibold">Iniciar Trámite</span>
              </Button>
              <Button onClick={() => setOpenForm('asesoramiento')} variant="outline" className="h-auto py-6 border-2 border-violet-300 text-violet-700 hover:bg-violet-50 shadow-sm flex flex-col items-center gap-2 whitespace-normal" data-testid="gestoria-btn-asesoramiento">
                <Scale className="h-7 w-7" />
                <span className="text-base font-semibold">Solicitar Asesoramiento</span>
              </Button>
            </div>
          </div>

          {/* Beneficios debajo */}
          <BeneficiosBanner />
        </div>
      </section>

      {/* SIGMA — fondo con degrade lineal vertical: blanco arriba, dorado solo en la última parte */}
      <section
        className="relative py-20 md:py-24 pb-28 overflow-hidden"
        id="sigma"
        style={{
          background: 'linear-gradient(to bottom, #ffffff 0%, #ffffff 65%, #fcd34d 88%, #d97706 100%)',
        }}
      >
        <div className="relative max-w-5xl mx-auto px-4">
          <SigmaBanner />
        </div>
      </section>

      {/* Modal con el formulario */}
      <Dialog open={!!openForm} onOpenChange={(o) => !o && setOpenForm(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="gestoria-form-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              {openForm === 'tramite' ? (<><ClipboardCheck className="h-5 w-5 text-violet-600" /> Iniciar Trámite</>) : (<><Scale className="h-5 w-5 text-violet-600" /> Solicitar Asesoramiento</>)}
            </DialogTitle>
            <DialogDescription>
              {openForm === 'tramite' ? 'Completá tus datos y te contactamos para avanzar con el trámite.' : 'Contanos tu consulta y uno de nuestros profesionales te responde en pocas horas.'}
            </DialogDescription>
          </DialogHeader>
          {openForm && <SolicitudForm tipoSolicitud={openForm} onClose={() => setOpenForm(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestoriaPage;
